'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Plus, X, TrendingUp, TrendingDown, Clock, Tag, Search, BookOpen, BarChart2, DollarSign, Percent } from 'lucide-react';

interface TradeJournalEntry {
  id: string;
  ticker: string;
  companyName: string;
  tradeType: 'LONG' | 'SHORT';
  instrument: 'EQ' | 'FUT' | 'CE' | 'PE';
  quantity: number;
  entryPrice: number;
  exitPrice: number | null;
  entryTime: string;
  exitTime: string | null;
  pnl: number | null;
  pnlPercent: number | null;
  charges: number;
  netPnl: number | null;
  setupTags: string[];
  notes: string;
  status: 'OPEN' | 'CLOSED';
  createdAt: string;
}

interface TradeFormData {
  ticker: string;
  companyName: string;
  tradeType: 'LONG' | 'SHORT';
  instrument: 'EQ' | 'FUT' | 'CE' | 'PE';
  quantity: string;
  entryPrice: string;
  exitPrice: string;
  entryTime: string;
  exitTime: string;
  charges: string;
  setupTags: string;
  notes: string;
  status: 'OPEN' | 'CLOSED';
}

const PRESET_TAGS = [
  'Breakout', 'Reversal', 'Trend Follow', 'Scalp', 'Swing',
  'Gap Up', 'Gap Down', 'Support Bounce', 'Resistance Reject',
  'VWAP', 'EMA Cross', 'RSI Divergence', 'News Play', 'Options Expiry'
];

const defaultForm: TradeFormData = {
  ticker: '', companyName: '', tradeType: 'LONG', instrument: 'EQ',
  quantity: '', entryPrice: '', exitPrice: '', entryTime: '', exitTime: '',
  charges: '0', setupTags: '', notes: '', status: 'OPEN',
};

function durationLabel(entryTime: string, exitTime: string | null): string {
  if (!exitTime) return 'Open';
  const diffMs = new Date(exitTime).getTime() - new Date(entryTime).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  const rem = mins % 60;
  return rem > 0 ? `${hrs}h ${rem}m` : `${hrs}h`;
}

function formatCurrency(val: number | null): string {
  if (val === null || val === undefined) return '—';
  return (val >= 0 ? '+' : '') + val.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function TradeJournalContent() {
  const { user } = useAuth();
  const supabase = createClient();

  const [trades, setTrades] = useState<TradeJournalEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<TradeFormData>(defaultForm);
  const [saving, setSaving] = useState(false);
  const [filterStatus, setFilterStatus] = useState<'ALL' | 'OPEN' | 'CLOSED'>('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  const fetchTrades = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from('trade_journal')
        .select('*')
        .eq('user_id', user.id)
        .order('entry_time', { ascending: false });

      if (fetchError) throw fetchError;

      setTrades((data || []).map((row: any) => ({
        id: row.id,
        ticker: row.ticker,
        companyName: row.company_name,
        tradeType: row.trade_type,
        instrument: row.instrument,
        quantity: row.quantity,
        entryPrice: parseFloat(row.entry_price),
        exitPrice: row.exit_price ? parseFloat(row.exit_price) : null,
        entryTime: row.entry_time,
        exitTime: row.exit_time,
        pnl: row.pnl !== null ? parseFloat(row.pnl) : null,
        pnlPercent: row.pnl_percent !== null ? parseFloat(row.pnl_percent) : null,
        charges: parseFloat(row.charges || 0),
        netPnl: row.net_pnl !== null ? parseFloat(row.net_pnl) : null,
        setupTags: row.setup_tags || [],
        notes: row.notes || '',
        status: row.status,
        createdAt: row.created_at,
      })));
    } catch (err: any) {
      setError(err.message || 'Failed to load trades');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { fetchTrades(); }, [fetchTrades]);

  const computePnl = (form: TradeFormData) => {
    const qty = parseFloat(form.quantity) || 0;
    const entry = parseFloat(form.entryPrice) || 0;
    const exit = parseFloat(form.exitPrice) || 0;
    const charges = parseFloat(form.charges) || 0;
    if (!exit || !entry || !qty) return { pnl: null, pnlPercent: null, netPnl: null };
    const rawPnl = form.tradeType === 'LONG'
      ? (exit - entry) * qty
      : (entry - exit) * qty;
    const pnlPercent = entry > 0 ? ((exit - entry) / entry) * 100 * (form.tradeType === 'SHORT' ? -1 : 1) : 0;
    return { pnl: rawPnl, pnlPercent, netPnl: rawPnl - charges };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setSaving(true);
    setError(null);

    const tags = selectedTags.length > 0 ? selectedTags
      : form.setupTags.split(',').map(t => t.trim()).filter(Boolean);

    const { pnl, pnlPercent, netPnl } = computePnl(form);

    const payload = {
      user_id: user.id,
      ticker: form.ticker.toUpperCase(),
      company_name: form.companyName,
      trade_type: form.tradeType,
      instrument: form.instrument,
      quantity: parseInt(form.quantity),
      entry_price: parseFloat(form.entryPrice),
      exit_price: form.exitPrice ? parseFloat(form.exitPrice) : null,
      entry_time: form.entryTime || new Date().toISOString(),
      exit_time: form.exitTime || null,
      pnl: form.status === 'CLOSED' ? pnl : null,
      pnl_percent: form.status === 'CLOSED' ? pnlPercent : null,
      charges: parseFloat(form.charges) || 0,
      net_pnl: form.status === 'CLOSED' ? netPnl : null,
      setup_tags: tags,
      notes: form.notes,
      status: form.status,
    };

    try {
      if (editingId) {
        const { error: updateError } = await supabase
          .from('trade_journal')
          .update(payload)
          .eq('id', editingId)
          .eq('user_id', user.id);
        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('trade_journal')
          .insert(payload);
        if (insertError) throw insertError;
      }
      setShowForm(false);
      setEditingId(null);
      setForm(defaultForm);
      setSelectedTags([]);
      fetchTrades();
    } catch (err: any) {
      setError(err.message || 'Failed to save trade');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (trade: TradeJournalEntry) => {
    setEditingId(trade.id);
    setSelectedTags(trade.setupTags);
    setForm({
      ticker: trade.ticker,
      companyName: trade.companyName,
      tradeType: trade.tradeType,
      instrument: trade.instrument,
      quantity: String(trade.quantity),
      entryPrice: String(trade.entryPrice),
      exitPrice: trade.exitPrice ? String(trade.exitPrice) : '',
      entryTime: trade.entryTime ? trade.entryTime.slice(0, 16) : '',
      exitTime: trade.exitTime ? trade.exitTime.slice(0, 16) : '',
      charges: String(trade.charges),
      setupTags: trade.setupTags.join(', '),
      notes: trade.notes,
      status: trade.status,
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!user || !confirm('Delete this trade entry?')) return;
    try {
      const { error: delError } = await supabase
        .from('trade_journal')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);
      if (delError) throw delError;
      fetchTrades();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const toggleTag = (tag: string) => {
    setSelectedTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  const addCustomTag = () => {
    const t = tagInput.trim();
    if (t && !selectedTags.includes(t)) {
      setSelectedTags(prev => [...prev, t]);
    }
    setTagInput('');
  };

  const filteredTrades = trades.filter(t => {
    const matchStatus = filterStatus === 'ALL' || t.status === filterStatus;
    const q = searchQuery.toLowerCase();
    const matchSearch = !q || t.ticker.toLowerCase().includes(q) || t.companyName.toLowerCase().includes(q);
    return matchStatus && matchSearch;
  });

  // Summary stats
  const closedTrades = trades.filter(t => t.status === 'CLOSED');
  const totalNetPnl = closedTrades.reduce((sum, t) => sum + (t.netPnl || 0), 0);
  const winners = closedTrades.filter(t => (t.netPnl || 0) > 0).length;
  const winRate = closedTrades.length > 0 ? (winners / closedTrades.length) * 100 : 0;
  const avgPnl = closedTrades.length > 0 ? totalNetPnl / closedTrades.length : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-foreground flex items-center gap-2">
            <BookOpen size={20} className="text-primary" />
            Trade Journal
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">Post-trade analysis — entry, exit, P&amp;L, duration &amp; setup tags</p>
        </div>
        <button
          onClick={() => { setShowForm(true); setEditingId(null); setForm(defaultForm); setSelectedTags([]); }}
          className="flex items-center gap-2 px-3 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 transition-colors"
        >
          <Plus size={15} />
          Log Trade
        </button>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: 'Total Trades', value: String(closedTrades.length), icon: BarChart2, color: 'text-info' },
          { label: 'Net P&L', value: formatCurrency(totalNetPnl), icon: DollarSign, color: totalNetPnl >= 0 ? 'text-positive' : 'text-negative' },
          { label: 'Win Rate', value: closedTrades.length > 0 ? `${winRate.toFixed(1)}%` : '—', icon: Percent, color: winRate >= 50 ? 'text-positive' : 'text-negative' },
          { label: 'Avg P&L', value: formatCurrency(avgPnl), icon: TrendingUp, color: avgPnl >= 0 ? 'text-positive' : 'text-negative' },
        ].map(stat => (
          <div key={stat.label} className="bg-card border border-border rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <stat.icon size={14} className={stat.color} />
              <span className="text-2xs text-muted-foreground uppercase tracking-wide">{stat.label}</span>
            </div>
            <p className={`text-base font-bold ${stat.color}`}>{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[180px]">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search ticker or company..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 bg-input border border-border rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
          />
        </div>
        <div className="flex items-center gap-1 bg-muted rounded-md p-0.5">
          {(['ALL', 'OPEN', 'CLOSED'] as const).map(s => (
            <button
              key={s}
              onClick={() => setFilterStatus(s)}
              className={`px-3 py-1 rounded text-xs font-medium transition-colors ${filterStatus === s ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-negative/10 border border-negative/30 text-negative text-sm rounded-md px-3 py-2">
          {error}
        </div>
      )}

      {/* Trade List */}
      {loading ? (
        <div className="space-y-2">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-card border border-border rounded-lg p-4 animate-pulse">
              <div className="h-4 bg-muted rounded w-1/4 mb-2" />
              <div className="h-3 bg-muted rounded w-1/2" />
            </div>
          ))}
        </div>
      ) : filteredTrades.length === 0 ? (
        <div className="bg-card border border-border rounded-lg p-10 text-center">
          <BookOpen size={32} className="text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No trades logged yet. Click "Log Trade" to add your first entry.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredTrades.map(trade => {
            const isProfit = (trade.netPnl ?? trade.pnl ?? 0) >= 0;
            const duration = durationLabel(trade.entryTime, trade.exitTime);
            return (
              <div
                key={trade.id}
                className="bg-card border border-border rounded-lg p-4 hover:border-primary/30 transition-colors group"
              >
                <div className="flex items-start justify-between gap-4">
                  {/* Left: Symbol + meta */}
                  <div className="flex items-start gap-3 min-w-0">
                    <div className={`w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 ${trade.tradeType === 'LONG' ? 'bg-positive/10' : 'bg-negative/10'}`}>
                      {trade.tradeType === 'LONG'
                        ? <TrendingUp size={15} className="text-positive" />
                        : <TrendingDown size={15} className="text-negative" />}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-sm text-foreground">{trade.ticker}</span>
                        <span className="text-2xs bg-muted text-muted-foreground px-1.5 py-0.5 rounded">{trade.instrument}</span>
                        <span className={`text-2xs px-1.5 py-0.5 rounded font-medium ${trade.status === 'OPEN' ? 'bg-warning/10 text-warning' : 'bg-muted text-muted-foreground'}`}>
                          {trade.status}
                        </span>
                      </div>
                      {trade.companyName && (
                        <p className="text-xs text-muted-foreground truncate mt-0.5">{trade.companyName}</p>
                      )}
                      {/* Entry / Exit */}
                      <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                        <span className="text-xs text-muted-foreground">
                          Entry: <span className="text-foreground font-medium">₹{trade.entryPrice.toLocaleString('en-IN')}</span>
                        </span>
                        {trade.exitPrice && (
                          <span className="text-xs text-muted-foreground">
                            Exit: <span className="text-foreground font-medium">₹{trade.exitPrice.toLocaleString('en-IN')}</span>
                          </span>
                        )}
                        <span className="text-xs text-muted-foreground">
                          Qty: <span className="text-foreground font-medium">{trade.quantity}</span>
                        </span>
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Clock size={11} />
                          {duration}
                        </span>
                      </div>
                      {/* Tags */}
                      {trade.setupTags.length > 0 && (
                        <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                          <Tag size={11} className="text-muted-foreground flex-shrink-0" />
                          {trade.setupTags.map(tag => (
                            <span key={tag} className="text-2xs bg-primary/10 text-primary px-1.5 py-0.5 rounded-full">{tag}</span>
                          ))}
                        </div>
                      )}
                      {trade.notes && (
                        <p className="text-xs text-muted-foreground mt-1 italic line-clamp-1">{trade.notes}</p>
                      )}
                    </div>
                  </div>

                  {/* Right: P&L + actions */}
                  <div className="flex flex-col items-end gap-2 flex-shrink-0">
                    {trade.status === 'CLOSED' && trade.netPnl !== null && (
                      <div className="text-right">
                        <p className={`text-base font-bold ${isProfit ? 'text-positive' : 'text-negative'}`}>
                          {formatCurrency(trade.netPnl)}
                        </p>
                        {trade.pnlPercent !== null && (
                          <p className={`text-xs ${isProfit ? 'text-positive' : 'text-negative'}`}>
                            {trade.pnlPercent >= 0 ? '+' : ''}{trade.pnlPercent.toFixed(2)}%
                          </p>
                        )}
                      </div>
                    )}
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => handleEdit(trade)}
                        className="text-xs text-muted-foreground hover:text-foreground px-2 py-1 rounded hover:bg-muted transition-colors"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(trade.id)}
                        className="text-xs text-negative/70 hover:text-negative px-2 py-1 rounded hover:bg-negative/10 transition-colors"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border sticky top-0 bg-card z-10">
              <h2 className="text-sm font-semibold text-foreground">
                {editingId ? 'Edit Trade' : 'Log New Trade'}
              </h2>
              <button
                onClick={() => { setShowForm(false); setEditingId(null); setForm(defaultForm); setSelectedTags([]); }}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              {/* Row 1: Ticker + Company */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">Ticker *</label>
                  <input
                    required
                    type="text"
                    value={form.ticker}
                    onChange={e => setForm(f => ({ ...f, ticker: e.target.value.toUpperCase() }))}
                    placeholder="NIFTY, RELIANCE..."
                    className="w-full px-3 py-2 bg-input border border-border rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                  />
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">Company Name</label>
                  <input
                    type="text"
                    value={form.companyName}
                    onChange={e => setForm(f => ({ ...f, companyName: e.target.value }))}
                    placeholder="Reliance Industries..."
                    className="w-full px-3 py-2 bg-input border border-border rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                  />
                </div>
              </div>

              {/* Row 2: Type + Instrument + Status */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">Direction</label>
                  <select
                    value={form.tradeType}
                    onChange={e => setForm(f => ({ ...f, tradeType: e.target.value as 'LONG' | 'SHORT' }))}
                    className="w-full px-3 py-2 bg-input border border-border rounded-md text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                  >
                    <option value="LONG">LONG</option>
                    <option value="SHORT">SHORT</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">Instrument</label>
                  <select
                    value={form.instrument}
                    onChange={e => setForm(f => ({ ...f, instrument: e.target.value as any }))}
                    className="w-full px-3 py-2 bg-input border border-border rounded-md text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                  >
                    <option value="EQ">EQ</option>
                    <option value="FUT">FUT</option>
                    <option value="CE">CE</option>
                    <option value="PE">PE</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">Status</label>
                  <select
                    value={form.status}
                    onChange={e => setForm(f => ({ ...f, status: e.target.value as 'OPEN' | 'CLOSED' }))}
                    className="w-full px-3 py-2 bg-input border border-border rounded-md text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                  >
                    <option value="OPEN">OPEN</option>
                    <option value="CLOSED">CLOSED</option>
                  </select>
                </div>
              </div>

              {/* Row 3: Qty + Entry + Exit */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">Quantity *</label>
                  <input
                    required
                    type="number"
                    min="1"
                    value={form.quantity}
                    onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))}
                    placeholder="100"
                    className="w-full px-3 py-2 bg-input border border-border rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                  />
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">Entry Price *</label>
                  <input
                    required
                    type="number"
                    step="0.05"
                    min="0"
                    value={form.entryPrice}
                    onChange={e => setForm(f => ({ ...f, entryPrice: e.target.value }))}
                    placeholder="0.00"
                    className="w-full px-3 py-2 bg-input border border-border rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                  />
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">Exit Price</label>
                  <input
                    type="number"
                    step="0.05"
                    min="0"
                    value={form.exitPrice}
                    onChange={e => setForm(f => ({ ...f, exitPrice: e.target.value }))}
                    placeholder="0.00"
                    className="w-full px-3 py-2 bg-input border border-border rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                  />
                </div>
              </div>

              {/* Row 4: Entry Time + Exit Time + Charges */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">Entry Time</label>
                  <input
                    type="datetime-local"
                    value={form.entryTime}
                    onChange={e => setForm(f => ({ ...f, entryTime: e.target.value }))}
                    className="w-full px-3 py-2 bg-input border border-border rounded-md text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                  />
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">Exit Time</label>
                  <input
                    type="datetime-local"
                    value={form.exitTime}
                    onChange={e => setForm(f => ({ ...f, exitTime: e.target.value }))}
                    className="w-full px-3 py-2 bg-input border border-border rounded-md text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                  />
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">Charges (₹)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.charges}
                    onChange={e => setForm(f => ({ ...f, charges: e.target.value }))}
                    placeholder="0"
                    className="w-full px-3 py-2 bg-input border border-border rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                  />
                </div>
              </div>

              {/* P&L Preview */}
              {form.status === 'CLOSED' && form.exitPrice && form.entryPrice && form.quantity && (() => {
                const { pnl, pnlPercent, netPnl } = computePnl(form);
                if (pnl === null) return null;
                const isPos = netPnl !== null && netPnl >= 0;
                return (
                  <div className={`rounded-md px-3 py-2 border ${isPos ? 'bg-positive/5 border-positive/20' : 'bg-negative/5 border-negative/20'}`}>
                    <p className="text-xs text-muted-foreground mb-1">P&L Preview</p>
                    <div className="flex items-center gap-4">
                      <span className="text-xs text-muted-foreground">Gross: <span className={`font-semibold ${isPos ? 'text-positive' : 'text-negative'}`}>{formatCurrency(pnl)}</span></span>
                      <span className="text-xs text-muted-foreground">Net: <span className={`font-semibold ${isPos ? 'text-positive' : 'text-negative'}`}>{formatCurrency(netPnl)}</span></span>
                      {pnlPercent !== null && <span className="text-xs text-muted-foreground">%: <span className={`font-semibold ${isPos ? 'text-positive' : 'text-negative'}`}>{pnlPercent >= 0 ? '+' : ''}{pnlPercent.toFixed(2)}%</span></span>}
                    </div>
                  </div>
                );
              })()}

              {/* Setup Tags */}
              <div>
                <label className="block text-xs text-muted-foreground mb-2">Setup Tags</label>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {PRESET_TAGS.map(tag => (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => toggleTag(tag)}
                      className={`text-2xs px-2 py-1 rounded-full border transition-colors ${selectedTags.includes(tag) ? 'bg-primary/20 border-primary text-primary' : 'bg-muted border-border text-muted-foreground hover:text-foreground'}`}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={e => setTagInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustomTag(); } }}
                    placeholder="Custom tag..."
                    className="flex-1 px-3 py-1.5 bg-input border border-border rounded-md text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                  />
                  <button
                    type="button"
                    onClick={addCustomTag}
                    className="px-3 py-1.5 bg-muted text-muted-foreground hover:text-foreground rounded-md text-xs transition-colors"
                  >
                    Add
                  </button>
                </div>
                {selectedTags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {selectedTags.map(tag => (
                      <span key={tag} className="flex items-center gap-1 text-2xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                        {tag}
                        <button type="button" onClick={() => toggleTag(tag)} className="hover:text-negative transition-colors"><X size={10} /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Notes */}
              <div>
                <label className="block text-xs text-muted-foreground mb-1">Notes / Analysis</label>
                <textarea
                  rows={3}
                  value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="What was the setup? What went right/wrong? Lessons learned..."
                  className="w-full px-3 py-2 bg-input border border-border rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring resize-none"
                />
              </div>

              {error && (
                <p className="text-xs text-negative">{error}</p>
              )}

              <div className="flex items-center justify-end gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => { setShowForm(false); setEditingId(null); setForm(defaultForm); setSelectedTags([]); }}
                  className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-5 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
                >
                  {saving ? 'Saving...' : editingId ? 'Update Trade' : 'Log Trade'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
